import { PrintAvoidBreak, PrintBreakAfter } from "../../designSystem/compositeComponents/Print";
import { Survey, SurveyReportDataSet, SurveySegment } from "../../amorg-common";
import { H1, H2, H3 } from "../../designSystem/typography/Heading";
import { Small } from "../../designSystem/typography/Body";
import { Flex, FlexItem } from "../../designSystem/compositeComponents/FlexHelpers";
import { ReportSummarySection } from "../workplaceassessments/[activityId]/_components/tabs/_components/ReportSummarySection";
import React from "react";
import { RadialPctChart } from "../../designSystem/graphs/RadialPctChart";
import styled from "styled-components";
import { PublicPageProvider } from "../_contexts/PublicPageContext";
import { SurveyQuestionAnswerScoreRadialChart } from "../../designSystem/graphs/SurveyAnswerScoreRadialChart";
import { AggregatedSurveyAnswerRatioBar, QuestionSurveyAnswerRatioBar } from "../workplaceassessments/[activityId]/_components/tabs/report/_components/SurveyAnswerRatioBar";


type PrintSurveyReportProps = {
    survey: Survey,
    data: SurveyReportDataSet,
    segmentFilter: SurveySegment[][],
}

export default function PrintSurveyReportPage(props: PrintSurveyReportProps): JSX.Element 
{
    return (
        <PublicPageProvider>
                <Flex flexDirection="column" gap={16}>
                    <H1>{props.survey.title}</H1>
                    <PrintBreakAfter>
                        <Flex flexDirection="column" gap={32}>
                            <Flex flexDirection="column" gap={4}>
                                <H2>Segmenter</H2>
                                <Flex flexDirection="row" gap={16} flexWrap="wrap">
                                    {props.segmentFilter.map( (segment, i) => (
                                        <FlexItem key={i}>
                                            {segment.map(s => s.value).join(", ")}
                                        </FlexItem>
                                    ))}
                                </Flex>
                            </Flex>

                            <Flex flexDirection="column" gap={4}>
                                <Flex flexDirection="row" gap={16} flexWrap="wrap">
                                    <ReportSummarySection survey={props.survey} data={props.data} />
                                </Flex>
                            </Flex>

                            <PrintAvoidBreak>
                                <Flex flexDirection="column" gap={4}>
                                    <H2>Indikatorer</H2>
                                    <Flex flexDirection="row" gap={16} flexWrap="wrap">
                                        
                                        {props.data && props.data.indicatorData.map(ir => 
                                            <Flex flexDirection="row" gap={48} key={ir.indicator}>
                                                <Flex flexDirection="row" alignItems="center" width="15%">
                                                    <RadialPctChart dim="120px" fontSize="9px" pct={ir.stats.score.agreePlusPct} />
                                                </Flex>
                                                <Flex flexDirection="column" width="85%">
                                                    <H3>{String(ir.indicator)}</H3>
                                                    <Small>
                                                        Indikatoren er anvendt på {ir.questions.length} spørgsmål,
                                                        der i alt er blevet besvaret {ir.stats.totalSelections} gange.
                                                    </Small>
                                                    <AggregatedSurveyAnswerRatioBar stats={ir.stats} />
                                                </Flex>
                                            </Flex>
                                        )}

                                    </Flex>
                                </Flex>
                            </PrintAvoidBreak>

                        </Flex>
                    </PrintBreakAfter>
                    
                    <H2>Spørgsmål fordelt på tema</H2>
                    
                    {props.data && props.data.themeData.map((theme, index) => {
                        return (
                            <PrintBreakAfter key={index}>
                                <Flex flexDirection="column" gap={16}>
                                    
                                    <Flex flexDirection="row" gap={48} alignItems="flex-start">
                                        <Flex flexDirection="row" alignItems="center" width="15%">
                                            <RadialPctChart dim="90px" fontSize="9px" pct={theme.stats.score.agreePlusPct} />
                                        </Flex>
                                        <Flex flexDirection="column" width="85%">
                                            <H3>{String(theme.theme)}</H3>
                                            <Small>
                                                Temaet indholder {theme.questions.length} spørgsmål, der i alt er blevet besvaret {theme.stats.totalSelections} gange.
                                            </Small>
                                            <AggregatedSurveyAnswerRatioBar stats={theme.stats} />
                                        </Flex>
                                    </Flex>

                                    <Flex flexDirection="column" gap={8}>
                                        
                                        {theme.questions.map( (reportItem, index) => {

                                            return (
                                                <PrintAvoidBreak key={index}>
                                                    <Flex flexDirection="row" gap={48} justifyContent="space-between">
                                                        <Flex flexDirection="row" alignItems="center" width="15%">
                                                            <QuestionNumber>#{reportItem.absoluteQuestionIndex + 1}</QuestionNumber>
                                                            <SurveyQuestionAnswerScoreRadialChart question={reportItem.question} stats={reportItem.stats} />
                                                        </Flex>    
                                                        <Flex flexDirection="column" gap={8} alignItems='flex-start' width="85%">
                                                            
                                                            <QuestionTitle>{reportItem.question.title}</QuestionTitle>
                                                            <FlexItem width="100%">
                                                                <QuestionSurveyAnswerRatioBar stats={reportItem.stats} question={reportItem.question} />
                                                            </FlexItem>
                                                        </Flex>
                                                    </Flex>
                                                </PrintAvoidBreak>
                                            );
                                        })}

                                    </Flex>
                                </Flex>
                            </PrintBreakAfter>
                        );
                    })}

                </Flex>
        </PublicPageProvider>
    );
}

const QuestionNumber = styled.div`
    color: rgba(191,191,191,1);
    font-size:1rem;
    font-weight: 700;
    line-height: 1.5rem;
    margin-right: 5px;
    flex:0;
`;

const QuestionTitle = styled.div`
    overflow-wrap: break-word;
    white-space: pre-wrap; 
    font-weight: 400;
`;